import type Map from 'ol/Map';
import type Point from 'ol/geom/Point';
import Draw from 'ol/interaction/Draw';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import type { AppStore } from '../core/store';
import type { MapTool } from '../core/types';
import { coordinateFromMap, formatCoordinate } from '../domain/coordinates';
import { elevationAtMapCoordinate, formatElevationReading } from '../elevation/elevationQuery';
import type { DemSource } from '../elevation/demSource';
import type { ElevationOverlay } from '../map/ElevationOverlay';
import { icon } from './icons';

const cursorStyle = new Style({ image: new CircleStyle({ radius: 5, fill: new Fill({ color: 'rgba(106,27,154,.25)' }), stroke: new Stroke({ color: '#6a1b9a', width: 2 }) }) });

/**
 * «Consultar cota»: one click or tap reads the terrain height at that point (desktop and mobile). The tap is consumed
 * by a Point Draw interaction with stopClick, so it never selects, deselects or edits anything; panning still works.
 */
export class ElevationQueryTool {
  private draw: Draw | null = null;
  private previousTool: MapTool | null = null;
  private last: readonly number[] | null = null;

  constructor(
    private readonly map: Map,
    private readonly store: AppStore,
    private readonly dem: DemSource,
    private readonly overlay: ElevationOverlay,
    private readonly readout: HTMLElement,
    private readonly openSource: () => void,
    /** Closes conflicting map editors (profile drawing, planning geometry edit) before activating. */
    private readonly beforeActivate: () => void = () => undefined,
  ) {
    readout.addEventListener('click', (event) => {
      const action = (event.target as Element).closest<HTMLElement>('[data-elevation-readout]')?.dataset.elevationReadout;
      if (action === 'close') this.deactivate();
      else if (action === 'source') this.openSource();
    });
    dem.onChange(() => { if (this.last) this.show(this.last); });
    // Picking any other map tool ends the query mode.
    store.subscribe((state, previous) => { if (this.draw && state.activeTool !== previous.activeTool && state.activeTool !== 'navigate') this.deactivate(false); });
    // Esc ends the query mode only; preventDefault keeps the global "Esc clears selection" from firing too.
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && this.draw) { event.preventDefault(); this.deactivate(); } });
  }

  get active(): boolean { return this.draw !== null; }

  toggle(): void { if (this.draw) this.deactivate(); else this.activate(); }

  activate(): void {
    if (this.draw) return;
    this.beforeActivate();
    void this.dem.load();
    const tool = this.store.getState().activeTool;
    this.previousTool = tool === 'navigate' ? 'select' : tool;
    if (tool !== 'navigate') this.store.update({ activeTool: 'navigate' }, { markDirty: false });
    this.draw = new Draw({ type: 'Point', stopClick: true, style: cursorStyle });
    this.draw.on('drawend', ({ feature }) => {
      const coordinate = (feature.getGeometry() as Point | undefined)?.getCoordinates();
      if (coordinate) this.show(coordinate);
    });
    this.map.addInteraction(this.draw);
    this.readout.hidden = false;
    this.readout.innerHTML = `<span class="elevation-readout-hint">${icon('elevation')}Toca o haz clic en el mapa para consultar la cota</span>${this.buttons()}`;
    this.syncButtons();
  }

  /** Ends the mode and removes every trace from the map; restores the previous tool. */
  deactivate(restoreTool = true): void {
    if (!this.draw) return;
    this.map.removeInteraction(this.draw); this.draw = null;
    this.overlay.setQuery(null); this.last = null;
    this.readout.hidden = true; this.readout.innerHTML = '';
    const previous = this.previousTool; this.previousTool = null;
    if (restoreTool && previous && this.store.getState().activeTool === 'navigate') this.store.update({ activeTool: previous }, { markDirty: false });
    this.syncButtons();
  }

  private show(coordinate: readonly number[]): void {
    this.last = coordinate;
    this.overlay.setQuery(coordinate);
    const reading = elevationAtMapCoordinate(this.dem, coordinate);
    const value = this.dem.status === 'error' ? 'Sin dato (modelo no disponible)' : formatElevationReading(reading);
    this.readout.innerHTML = `<span class="elevation-readout-value"><small>Cota DEM</small><b data-elevation-value>${value}</b></span><span class="elevation-readout-coordinate">${formatCoordinate(coordinateFromMap(coordinate), { degrees: true })}</span>${this.buttons()}`;
  }

  private buttons(): string {
    return `<button type="button" class="status-source" data-elevation-readout="source" title="Fuente y limitaciones de la cota">GLO‑30</button><button type="button" class="ghost-icon" data-elevation-readout="close" aria-label="Terminar consulta de cota" title="Terminar (Esc)">${icon('close')}</button>`;
  }

  private syncButtons(): void {
    document.querySelectorAll<HTMLElement>('.tool-button[data-elevation-tool="query"]').forEach((button) => { button.classList.toggle('active', this.active); button.setAttribute('aria-pressed', String(this.active)); });
  }
}
